import { useDebounce } from '@uidotdev/usehooks';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Badge,
  Button,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetTitle,
  Text,
  Textarea,
  toast,
} from '@eluve/components';

import {
  CLINICAL_TASK_CATEGORIES,
  CLINICAL_TASK_CATEGORY_ICONS,
  CLINICAL_TASK_CATEGORY_LABELS,
  CLINICAL_TASK_PRIORITIES,
  CLINICAL_TASK_PRIORITY_LABELS,
  type ClinicalTaskCategory,
  categoryIcon,
  categoryLabel,
} from './constants';
import type {
  ClinicalTaskEdit,
  PendingClinicalTask,
  PractitionerOption,
} from './useClinicalTasksPanelState';

// Autosave cadence — matches the note editor's DEBOUNCE_MS.
const DEBOUNCE_MS = 500;

/** UI-shaped field values ('' = unset where the edit payload uses null). */
type FieldValues = {
  description: string;
  category: string;
  priority: string;
  dueDate: string;
  ownerReference: string;
};

const toEdit = (v: FieldValues): ClinicalTaskEdit => ({
  description: v.description.trim(),
  category: v.category,
  priority: v.priority,
  dueDate: v.dueDate || null,
  ownerReference: v.ownerReference || null,
});

const sameEdit = (a: ClinicalTaskEdit, b: ClinicalTaskEdit): boolean =>
  a.description === b.description &&
  a.category === b.category &&
  a.priority === b.priority &&
  a.dueDate === b.dueDate &&
  a.ownerReference === b.ownerReference;

/** One "Label | control" row in the DETAILS section. */
const FieldRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-start gap-3">
    <Text size="sm" color="tertiary" className="w-28 shrink-0 pt-2">
      {label}
    </Text>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

/** Uppercase section heading (SUMMARY / DETAILS). */
const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <Text
    size="xs"
    weight="semibold"
    color="tertiary"
    className="tracking-wide uppercase"
  >
    {children}
  </Text>
);

/**
 * Edit detail view for one pending Clinical Task — a right-side slide-over
 * (same Sheet motion as the findings review surface). Edits AUTOSAVE like the
 * rest of Eluve's forms: chips/selects/date persist on change, the summary
 * after a typing pause (or blur), each write optimistically updating the card
 * preview behind the sheet. A failed save toasts and reverts the field to the
 * last stored value. Closing (X / overlay / Esc) keeps everything saved and
 * leaves the task pending. Sticky footer: Accept (flushes any in-flight edit,
 * then accepts) and Delete (rejects the draft). Reassignment changes only the
 * owner — the creator keeps visibility via the requester policy rule.
 */
export const EditClinicalTaskSheet: FC<{
  task: PendingClinicalTask;
  practitioners: PractitionerOption[];
  onSave: (edit: ClinicalTaskEdit) => Promise<boolean>;
  onAccept: () => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  onClose: () => void;
}> = ({ task, practitioners, onSave, onAccept, onDelete, onClose }) => {
  const [values, setValues] = useState<FieldValues>(() => ({
    description: task.description,
    category: task.category ?? 'general',
    priority: task.priority ?? 'routine',
    dueDate: task.dueDate ?? '',
    ownerReference: task.ownerReference ?? '',
  }));
  // Same values, readable inside the async save chain without stale closures.
  const valuesRef = useRef(values);
  const lastSavedRef = useRef<ClinicalTaskEdit>(toEdit(values));
  // Saves run strictly one at a time, in order — a select change while the
  // summary save is in flight queues behind it.
  const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const [busy, setBusy] = useState(false);

  /** Persist the current values if they differ from the last stored state. */
  const queueSave = useCallback((): Promise<boolean> => {
    saveChainRef.current = saveChainRef.current.then(async () => {
      const v = valuesRef.current;
      // Summary is required — an empty draft never autosaves; the field keeps
      // the user's in-progress state until it's valid again.
      if (!v.description.trim()) return true;
      const edit = toEdit(v);
      if (sameEdit(edit, lastSavedRef.current)) return true;
      if (await onSave(edit)) {
        lastSavedRef.current = edit;
        return true;
      }
      // Toast + revert — the sheet always reflects what is actually stored.
      const s = lastSavedRef.current;
      const reverted: FieldValues = {
        description: s.description,
        category: s.category,
        priority: s.priority ?? 'routine',
        dueDate: s.dueDate ?? '',
        ownerReference: s.ownerReference ?? '',
      };
      valuesRef.current = reverted;
      setValues(reverted);
      toast.error('Could not save the change.');
      return false;
    });
    return saveChainRef.current;
  }, [onSave]);

  /** Update fields; `immediate` persists now (chips/selects/date) — the
   *  summary instead rides the debounce below. */
  const setField = (patch: Partial<FieldValues>, immediate: boolean) => {
    const next = { ...valuesRef.current, ...patch };
    valuesRef.current = next;
    setValues(next);
    if (immediate) void queueSave();
  };

  // The summary autosaves once typing pauses (and on blur, below).
  const debouncedDescription = useDebounce(values.description, DEBOUNCE_MS);
  useEffect(() => {
    if (
      debouncedDescription.trim() &&
      debouncedDescription.trim() !== lastSavedRef.current.description
    ) {
      void queueSave();
    }
  }, [debouncedDescription, queueSave]);

  const accept = async () => {
    if (!valuesRef.current.description.trim()) {
      toast.error('A description is required.');
      return;
    }
    setBusy(true);
    try {
      // Flush anything not yet saved (e.g. summary inside the debounce
      // window); a failed save blocks the accept.
      if (!(await queueSave())) return;
      if (await onAccept()) {
        toast.success('Task accepted.');
        onClose();
      } else {
        toast.error('Could not accept the task.');
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const rejected = await onDelete();
      if (rejected) {
        toast.success('Task rejected.');
        onClose();
      } else {
        toast.error('Could not reject the task.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-[480px] flex-col gap-0 sm:max-w-[480px]"
        data-testid="clinical-task-edit-sheet"
      >
        <SheetTitle className="sr-only">Edit task</SheetTitle>

        <div className="border-b border-border-primary p-4">
          <Badge
            variant="secondary"
            className="gap-1 bg-bg-indigo-subtle-primary text-text-indigo-subtle"
          >
            <Icon name={categoryIcon(values.category)} size="xs" />
            {categoryLabel(values.category)}
          </Badge>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
          <div className="flex flex-col gap-2">
            <SectionLabel>Summary</SectionLabel>
            <Textarea
              aria-label="Summary"
              value={values.description}
              onChange={(e) =>
                setField({ description: e.currentTarget.value }, false)
              }
              onBlur={() => void queueSave()}
              className="font-medium"
            />
          </div>

          <div className="flex flex-col gap-4">
            <SectionLabel>Details</SectionLabel>

            <FieldRow label="Type">
              <div
                role="radiogroup"
                aria-label="Type"
                className="flex flex-wrap gap-2"
              >
                {CLINICAL_TASK_CATEGORIES.map((c) => {
                  const selected = c === values.category;
                  return (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setField({ category: c }, true)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                        selected
                          ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary text-text-indigo-subtle'
                          : 'border-border-primary bg-white text-text-secondary hover:bg-bg-secondary'
                      }`}
                    >
                      <Icon name={CLINICAL_TASK_CATEGORY_ICONS[c]} size="xs" />
                      {CLINICAL_TASK_CATEGORY_LABELS[c as ClinicalTaskCategory]}
                    </button>
                  );
                })}
              </div>
            </FieldRow>

            <FieldRow label="Status">
              {/* Read-only: a pending task is always Draft; the transition is
                  Accept / Delete below. */}
              <div className="rounded-md border border-border-primary bg-bg-secondary p-2.5">
                <Text size="sm" color="secondary">
                  Draft
                </Text>
              </div>
            </FieldRow>

            <FieldRow label="Priority">
              <Select
                value={values.priority}
                onValueChange={(priority) => setField({ priority }, true)}
              >
                <SelectTrigger aria-label="Priority">
                  <SelectValue placeholder="Pick a priority" />
                </SelectTrigger>
                <SelectContent>
                  {CLINICAL_TASK_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {CLINICAL_TASK_PRIORITY_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="For (patient)">
              {/* Read-only: the task's patient is bound to the appointment. */}
              <div className="flex items-center gap-2 rounded-md border border-border-primary p-2.5">
                <Icon name="User" size="sm" />
                <Text size="sm" weight="medium">
                  {task.forDisplay ?? '—'}
                </Text>
              </div>
            </FieldRow>

            <FieldRow label="Assigned to">
              <Select
                value={values.ownerReference}
                onValueChange={(ownerReference) =>
                  setField({ ownerReference }, true)
                }
              >
                <SelectTrigger aria-label="Assignee">
                  <SelectValue placeholder="Pick an assignee" />
                </SelectTrigger>
                <SelectContent>
                  {practitioners.map((p) => (
                    <SelectItem key={p.reference} value={p.reference}>
                      {p.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            <FieldRow label="Planned date">
              <Input
                type="date"
                aria-label="Planned date"
                value={values.dueDate}
                onChange={(e) => setField({ dueDate: e.target.value }, true)}
              />
            </FieldRow>
          </div>
        </div>

        <div className="flex gap-2 border-t border-border-primary p-4">
          <Button variant="destructive" disabled={busy} onClick={remove}>
            <Icon name="Trash2" size="sm" />
            Delete
          </Button>
          <Button className="flex-1" disabled={busy} onClick={accept}>
            {busy ? 'Accepting…' : 'Accept'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
